import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import type { UserAccount } from '../types/auth';
import * as authService from '../services/authService';

interface AuthContextValue {
  user: UserAccount | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  loginWithIdToken: (idToken: string, provider: 'google' | 'apple') => Promise<void>;
  signOut: () => Promise<void>;
  clearError: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserAccount | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const location = useLocation();

  // Initialize auth state on mount - restore from localStorage
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    setIsLoading(false);
  }, []);

  const loginWithIdToken = async (idToken: string, provider: 'google' | 'apple') => {
    setError(null);
    setIsLoading(true);
    try {
      const user = await authService.loginWithIdToken(idToken, provider);
      setUser(user);
    } catch (err) {
      setError(err instanceof Error ? err.message : '登入時發生錯誤，請稍後再試');
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const signOut = async () => {
    setError(null);
    try {
      await authService.signOut();
      setUser(null);
      // Redirect to homepage after sign-out (FR-013)
      if (location.pathname !== '/') {
        navigate('/');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : '登出時發生錯誤，請稍後再試');
    }
  };

  const clearError = () => setError(null);

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        error,
        loginWithIdToken,
        signOut,
        clearError,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}
